import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import * as api from 'core/api';
import * as utils from 'core/utils';
import * as flights from 'core/flights';
import FlightsBookingPassengersFormComponent from './form';
import PaymentCouponComponent from 'src/components/payment/coupon';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component({
    components: {
        'v-flights-booking-passengers-form': FlightsBookingPassengersFormComponent,
        'v-payment-coupon': PaymentCouponComponent
    }
})
export default class FlightsBookingPassengersComponent extends Vue {
    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    flightBookingDetails: api.FlightBookingDetails;

    @Prop()
    gaParams: utils.GoogleAnalyticsParams;

    @Prop()
    gaAlreadySubmitted: string[];

    @Prop()
    searchId: string;

    @Prop()
    searchResultsUrl: string;

    @Prop()
    v: any;

    fareCoverMarginBottom: number = 0;
    contactsPhonePlaceholder: string | null = null;
    emailAfterInputCompleted: string = '';

    tooltipActivator: string | null = null;
    tooltipText: string | null = null;
    tooltipVisible: boolean = false;
    tooltipTimeout: number | null = null;


    get stepOneBrands() {
        return this.reservation.brandedProductKits?.filter(b => {
            return b.stepOnForm === 1 && b.kitTarget !== 'AdditionalPopup';
        }) ?? [];
    }

    get freezePlace(): string {
        if (this.flightBookingDetails && this.flightBookingDetails?.prebookingTimeLimitMinutes
            && !this.gaAlreadySubmitted.includes('flights.booking.info.freeze.place')) {
            (<any>this).$ga.sendEvent('flights.booking', 'info.freeze.place', 'flights.booking.info.freeze.place', this.gaParams);

            this.gaAlreadySubmitted.push('flights.booking.info.freeze.place');
        }

        return i18next.t('label_freeze_place', { i: this.flightBookingDetails ? this.flightBookingDetails?.prebookingTimeLimitMinutes : 0 });
    }

    get hasFreeBaggage(): boolean {
        return utils.Baggage.hasFreeBaggage(this.reservation.selectedFlight!);
    }

    get hasSelectedPaidAnyBaggage() {
        return this.reservation.hasSelectedAnyCustomBaggage;
    }

    get productsOfTypeInsuranceForPassengersStep() {
        return this.reservation.productsOfTypeInsurance.filter(({ product }) =>
            product.stepNumber === 1
        );
    }

    get productOfTypeServiceOrPackageForPassengersStep() {
        return this.reservation.productsOfTypeServiceOrPackage.filter(({ product }) => 
            product.stepNumber === 1 && (product.flightId == this.reservation.selectedFlightIdForProducts || product.flightId == null)
        );
    }

    get seatsLeftWarning() {
        return i18next.t('label_seats_left_warning', <any>{ i: i18next.t('numerous_seat', <any>{ count: this.reservation.passengers.length }) });
    }

    get submitButtonLabel() {
        return this.reservation?.trains.length
            ? `${i18next.t('button_next')}: ${i18next.t('label_seat_selection').toLowerCase()}`
            : i18next.t('button_continue');
    }

    getDestinationNameById(id) {
        const destination = this.flightBookingDetails.destinations[id];
        let destinationName = '';

        if (destination && destination.city && destination.city.localizedName) {
            destinationName = destination.city.localizedName;
        }

        if (destinationName) {
            return destinationName;
        } else {
            return '';
        }
    }

    getBaggageOptions(baggageItems: api.CustomBaggageItem[]): any {
        const result: { label: string; value: string }[] = [];

        baggageItems.forEach((baggage: api.CustomBaggageItem) => {
            const item = {
                label: i18next.t('label_baggage_without_additional'),
                value: baggage.id
            };

            if (baggage.bagsQuantity > 0) {
                item.label = i18next.t('numerous_baggage_piece', <any>{ count: baggage.bagsQuantity });
                if (baggage.bagsWeight && baggage.bagsWeight != '0') {
                    item.label += ' (' + baggage.bagsWeight + ' ' + i18next.t('label_kg') + ')';
                }
                if (baggage.price > 0) {
                    item.label += ': ' + (<any>this.$options.filters).price(baggage.price);
                } else {
                    item.label += ': ' + i18next.t('label_price_free');
                }
            }

            result.push(item);
        });

        return result;
    }

    getBonusesLabel(): string {
        const bonuses = Math.round(this.reservation.selectedFlight!.billingPrice * 1 / 100);
        return i18next.t('label_bonuses_for_order', <any>{ i: i18next.t('numerous_bonus', <any>{ count: bonuses }) });
    }

    getBrandDescription(brand: api.FlightBrand): string {
        return brand && brand.descriptionUI ? brand.descriptionUI.replace('"fa-ul"', '"ul-icons"') : '';
    }

    getBrandLabel(): string {
        return i18next.t('label_choose_of_our_clients', <any>{ i: 30 });
    }

    getBrandPrice(flight: api.Flight): string {
        if (flight.uniqueId === this.reservation.selectedFlightIdForProducts) {
            return (<any>this.$options.filters).price(flight.billingPrice);
        } else {
            return (<any>this.$options.filters).price(flight.billingPrice - this.reservation.selectedFlight!.billingPrice, true);
        }
    }

    getBrandGroupPrice(flight: api.Flight, brandGroup: flights.FlightGroupModel): string {
        if (flight.uniqueId === brandGroup.selectedFlightId) {
            return (<any>this.$options.filters).price(flight?.billingPrice, false, flight?.currencyCode);
        } else {
            return (<any>this.$options.filters).price(flight?.billingPrice - this.reservation.getFlightBillingPrice(brandGroup.selectedFlightId), true, flight?.currencyCode);
        }
    }

    onBaggageChange() {
        // Next tick is required to access changed value in the reservation
        this.$nextTick(() => {
            const oldBaggagePrice = this.reservation.customBaggagePrice;


            if (oldBaggagePrice != this.reservation.customBaggagePrice) {
                if (this.gaAlreadySubmitted.indexOf('flights.booking.baggage.changed') < 0) {
                    (<any>this).$ga.sendEvent('flights.booking', 'baggage.changed', 'flights.booking.baggage.changed', this.gaParams);

                    this.gaAlreadySubmitted.push('flights.booking.baggage.changed');
                }
            }
            const oldBaggageChoiceType = this.reservation.baggageChoiceType;

            if (this.reservation.hasSelectedAnyCustomBaggage) {
                this.reservation.baggageChoiceType = api.BaggageChoiceType.manualAccepted;
            } else {
                this.reservation.baggageChoiceType = api.BaggageChoiceType.manualDenied;
            }

            console.log('baggageChoiceType ' + oldBaggageChoiceType + ' > ' + this.reservation.baggageChoiceType);
        });
    }

    validatePassengerByIndexAndUpsertFFDCookie(index: number) {
        const passenger = this.reservation.passengers[index];
        let passengerIsFullfilled = !!passenger.gender
            && !!passenger.lastName
            && !!passenger.firstName
            && !!passenger.birthday
            && !!passenger.documentNumber;

        if (!passenger.documentValidTillVisible) {
            passengerIsFullfilled = passengerIsFullfilled && !!passenger.middleName;
        }
        if (passenger.documentValidTillVisible) {
            passengerIsFullfilled = passengerIsFullfilled && !!passenger.documentValidTill;
        }
        if (passengerIsFullfilled) {
            const vPassenger = this.v.passengers.$each[index];

            vPassenger.gender.$touch();
            vPassenger.lastName.$touch();
            vPassenger.firstName.$touch();
            vPassenger.birthday.$touch();
            vPassenger.documentNumber.$touch();

            let passengerIsValid = !vPassenger.gender.$error
                && !vPassenger.lastName.$error
                && !vPassenger.firstName.$error
                && !vPassenger.birthday.$error
                && !vPassenger.documentNumber.$error;

            if (!passenger.documentValidTillVisible) {
                vPassenger.middleName.$touch();
                passengerIsValid = passengerIsValid && !vPassenger.middleName.$error;
            }
            if (passenger.documentValidTillVisible) {
                vPassenger.documentValidTill.$touch();
                passengerIsValid = passengerIsValid && !vPassenger.documentValidTill.$error;
            }
            if (passengerIsValid) {
                const passengers: (flights.PassengerModel | null)[] = [];

                this.reservation.passengers.forEach((passengerElement, passengerInedx) => {
                    if (index == passengerInedx) {
                        passengers.push(passengerElement);
                    } else {
                        passengers.push(null);
                    }
                });
                utils.Cookies.g2sFFDUpsert({ passengers });
            }
        }
    }

    onContactsEmailBlur() {
        if (this.reservation.contacts.email) {
            this.v.contacts.email.$touch();
            if (!this.v.contacts.email.$error) {
                this.emailAfterInputCompleted = this.reservation.contacts.email;
                utils.Cookies.g2sFFDUpsert({ email: this.reservation.contacts.email });
            }
        }
    }

    onContactsEmailInput() {
        this.$emit('change');
        this.reservation.contacts.email = utils.String.disabledCyrillic(
            this.reservation.contacts.email,
            () => {
                this.showTooltip(
                    (<any>this.$refs).contactsEmail.$el,
                    i18next.t('error_letters_latin_numbers_symbols_only_no_specifics')
                );
            },
        );
        // Hack to render new value
        (<any>this.$refs).contactsEmail.lazyValue = this.reservation.contacts.email;
    }

    onContactsPhoneFocus() {
        if (!this.reservation.contacts.phone) {
            // this.contactsPhonePlaceholder = '+7';
        }
    }

    onContactsPhoneBlur() {
        if (!this.reservation.contacts.phone) {
            this.contactsPhonePlaceholder = null;
        }
        if (this.reservation.contacts.phone) {
            this.v.contacts.phone.$touch();
            if (!this.v.contacts.phone.$error) {
                utils.Cookies.g2sFFDUpsert({ phone: this.reservation.contacts.phone });
            }
        }
    }

    onContactsPhoneInput() {
        this.reservation.contacts.phone = utils.String.normalizePhone(
            this.reservation.contacts.phone
        );
        // Hack to render new value
        (<any>this.$refs).contactsPhone.lazyValue = this.reservation.contacts.phone;
        this.$emit('change');
    }

    onFlightSelect(flight: flights.FlightModel, index: number) {
        this.$emit('brand-accum', index, flight.uniqueId);
        
        const brandSelectNum = this.gaAlreadySubmitted.includes('flights.booking.btn.brands') ? '2' : '';
        if (flight.billingPrice > this.reservation.selectedFlightBillingPrice) {
            this.$emit('brand-change', 'upgrade' + brandSelectNum);
        } else if (flight.billingPrice < this.reservation.selectedFlightBillingPrice) {
            this.$emit('brand-change', 'downgrade' + brandSelectNum);
        }

        this.reservation.selectedFlightId = flight.uniqueId;
        const oldBrandChoiceType = this.reservation.brandChoiceType;
        
        if (this.reservation.defaultFlight
            && this.reservation.defaultFlight.uniqueId === flight.uniqueId) {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.manualDefault;
        } else {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.manualUpgrade;
        }

        console.log('brandChoiceType ' + oldBrandChoiceType + ' > ' + this.reservation.brandChoiceType);
    }

    onBrandGroupSelect(brandGroupIndex: number, flight: flights.FlightModel) {
        const brandSelectNum = this.gaAlreadySubmitted.includes('flights.booking.btn.brands') ? '2' : '';
        if (flight.billingPrice > this.reservation.brandGroups[brandGroupIndex].selectedFlight!.billingPrice) {
            this.reservation.brandGroups[brandGroupIndex].brandChangeState = 'upgrade' + brandSelectNum;
        } else if (flight.billingPrice < this.reservation.brandGroups[brandGroupIndex].selectedFlight!.billingPrice) {
            this.reservation.brandGroups[brandGroupIndex].brandChangeState = 'downgrade' + brandSelectNum;
        }

        this.reservation.brandGroups[brandGroupIndex].selectedFlightId = flight.uniqueId;
        const oldBrandChoiceType = this.reservation.brandChoiceType;

        if (this.reservation.brandGroups[brandGroupIndex].defaultFlightId === flight.uniqueId) {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.manualDefault;
        } else {
            this.reservation.brandChoiceType = api.FlightBrandChoiceType.manualUpgrade;
        }

        console.log('brandChoiceType ' + oldBrandChoiceType + ' > ' + this.reservation.brandChoiceType);

        const brandNum = this.gaAlreadySubmitted.includes('flights.booking.btn.brands') ? '2' : '';
        if (!this.gaAlreadySubmitted.includes('flights.booking.brands.changed' + brandNum)) {
            (<any>this).$ga.sendEvent('flights.booking', 'brands.changed' + brandNum, 'flights.booking.brands.changed' + brandNum, this.gaParams);
            this.gaAlreadySubmitted.push('flights.booking.brands.changed' + brandNum);
        }
    }

    onPassengerChange() {
        this.$emit('change');
    }

    onProductDetails(product: flights.ProductModel) {
        this.$emit('product-details', product);
    }

    onFlightTerms() {
        if (this.reservation.selectedFlight) {
            this.$emit('flight-terms', this.reservation.selectedFlight.uniqueId);
        }
    }

    onProductSelectionChange(product: flights.ProductModel, value: boolean) {
        this.$emit('product-selection-change', product, value);
    }

    onSubmit() {
        if (this.productOfTypeServiceOrPackageForPassengersStep.some(p => p.needToSelect) || this.stepOneBrands.some(b => b.needToSelect)) {
            this.reservation.needToSelectToGoToNextStep = true;
            (<any>this.$root).$refs.dialogMessage.show({
                text: i18next.t('label_error_need_select_modal'),
                btnOkLabel: 'OK',
                persistent: true,
                btnOkColorClass: 'btn-alternate',
                btnOkHandler() {
                    (<any>this).$refs.dialog.isActive = false;
                }
            });
            return;
        } else {
            this.reservation.needToSelectToGoToNextStep = false;
        }

        this.v.contacts.$touch();
        this.v.passengers.$touch();

        if (this.v.contacts.$error || this.v.passengers.$error) {

            let title = i18next.t('error');
            let error = '';

            this.reservation.passengers.forEach((passenger, index) => {
                if (!error) {
                    if (!error
                        && this.v.passengers.$each[index].lastName.$error) {
                        error = i18next.t('error_name_last_wrong');
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.surname.error', 'flights.booking.step1.surname.error', this.gaParams);
                        }
                    }
                    if (!error
                        && this.v.passengers.$each[index].firstName.$error) {
                        error = i18next.t('error_name_first_wrong');
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.name.error', 'flights.booking.step1.name.error', this.gaParams);
                        }
                    }
                    if (!error) {
                        if (!this.v.passengers.$each[index].middleName.validatePassengerNameLast) {
                            error = i18next.t('error_name_middle_required');
                            if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                                (<any>this).$ga.sendEvent('flights.booking', 'step1.patronymic.error', 'flights.booking.step1.patronymic.error', this.gaParams);
                            }
                        }
                        if (!error
                            && this.v.passengers.$each[index].middleName.$error) {
                            error = i18next.t('error_name_middle_wrong');
                            if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                                (<any>this).$ga.sendEvent('flights.booking', 'step1.patronymic.error', 'flights.booking.step1.patronymic.error', this.gaParams);
                            }
                        }
                    }
                    if (!error
                        && this.v.passengers.$each[index].gender.$error) {
                        error = i18next.t('error_gender_not_selected');
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.gender.error', 'flights.booking.step1.gender.error', this.gaParams);
                        }
                    }
                    if (!error) {
                        if (!this.v.passengers.$each[index].birthday.validateBirthday) {
                            if (passenger.type === 'adult') {
                                if (!this.v.passengers.$each[index].birthday.validateBirthdayMin) {
                                    error = i18next.t('error_birthday_adult_old');
                                } else {
                                    error = i18next.t('error_birthday_adult_wrong');
                                }
                            } else if (passenger.type === 'child') {
                                error = i18next.t('error_birthday_child_wrong');
                            } else if (passenger.type === 'infant') {
                                error = i18next.t('error_birthday_infant_wrong');
                            }
                            if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                                (<any>this).$ga.sendEvent('flights.booking', 'step1.date.birth.error', 'flights.booking.step1.date.birth.error', this.gaParams);
                            }
                        }
                        if (!error
                            && !this.v.passengers.$each[index].birthday.isValidDate) {
                            error = i18next.t('error_birthday_invalid');
                        }
                        if (!error
                            && this.v.passengers.$each[index].birthday.$error) {
                            error = i18next.t('error_birthday_empty');
                            if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                                (<any>this).$ga.sendEvent('flights.booking', 'step1.date.birth.error', 'flights.booking.step1.date.birth.error', this.gaParams);
                            }
                        }
                    }

                    if (!error) {
                        if (!this.v.passengers.$each[index].citizenship.isCitizenshipAccept) {
                            error = i18next.t('error_citizenship_wrong_fare');
                        }
                        if (!error
                            && (this.v.passengers.$each[index].citizenship.$error
                            || !this.v.passengers.$each[index].citizenship.isCitizenshipFilled)) {
                            error = i18next.t('error_citizenship_empty');
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.citizenship.error', 'flights.booking.step1.citizenship.error', this.gaParams);
                        }
                    }

                    if (!error
                        && this.v.passengers.$each[index].documentNumber.$error) {
                        if (!this.v.passengers.$each[index].documentNumber.required) {
                            error = i18next.t('error_document_number_empty');
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.minLength) {
                            error = i18next.t('error_document_number_too_short');
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.maxLength) {
                            error = i18next.t('error_document_number_too_long');
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.validateCertificateArmenia) {
                            error = 'Оформление билетов возможно только по заграничному паспорту.';
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.validateCertificateAge) {
                            error = 'Cтарше 14 лет. Введите серию, номер российского паспорта или заграничного паспорта РФ.';
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.validateDocumentForeign) {
                            error = 'Для заграничного паспорта РФ введите 9 цифр или выберите другое гражданство.';
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.validateCertificateSymbol) {
                            error = i18next.t('error_document_certificate_notvalid');
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentNumber.validateDocumentNumber) {
                            error = i18next.t('error_document_number_notvalid');
                        }
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.passport.error', 'flights.booking.step1.passport.error', this.gaParams);
                        }
                    }

                    if (!error
                        && this.v.passengers.$each[index].documentValidTill.$error) {
                        if (!this.v.passengers.$each[index].documentValidTill.validateDocumentValidTill) {
                            error = i18next.t('error_document_valid_till_wrong', <any>{ i: passenger.validationConstraints.minDocumentValidTill.format('L') });
                        }
                        if (!error
                            && !this.v.passengers.$each[index].documentValidTill.isValidDate) {
                            error = i18next.t('error_document_valid_till_invalid');
                        }
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.passport.date.error', 'flights.booking.step1.passport.date.error', this.gaParams);
                        }
                    }

                    if (!error
                        && this.v.passengers.$each[index].cardAirlineId.$error) {
                        error = i18next.t('error_passenger_card_airline_id_required');
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.card.miles.error', 'flights.booking.step1.card.miles.error', this.gaParams);
                        }
                    }

                    if (!error
                        && this.v.passengers.$each[index].card.$error) {
                        if (!this.v.passengers.$each[index].card.maxLength) {
                            error = i18next.t('error_passenger_card_maxlength');
                        } else if (!this.v.passengers.$each[index].card.onlyOneUniqueCard) {
                            error = i18next.t('error_passenger_card_used_more_than_once');
                        }
                        if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                            (<any>this).$ga.sendEvent('flights.booking', 'step1.card.miles.error', 'flights.booking.step1.card.miles.error', this.gaParams);
                        }
                    }

                    if (error) {
                        error = i18next.t('label_passenger_with_number', <any>{ i: index + 1 }) + ': ' + error;
                    }
                }
            });

            if (!error
                && this.v.contacts.email.$error) {
                error = i18next.t('error_email_wrong');
                if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'step1.email.error', 'flights.booking.step1.email.error', this.gaParams);
                }
            }

            if (!error
                && this.v.contacts.phone.$error) {
                error = i18next.t('error_phone_wrong');
                if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'step1.phone.error', 'flights.booking.phone.error', this.gaParams);
                }
            }

            if (!error
                && !this.v.passengers.validationPassengersBirthday) {
                error = i18next.t('error_has_adult_wrong');
                if (this.gaAlreadySubmitted.includes('flights.booking.input.started')) {
                    (<any>this).$ga.sendEvent('flights.booking', 'step1.passengers.error', 'flights.booking.passengers.error', this.gaParams);
                }
            }

            if (!error) {
                title = i18next.t('error'); // Hide title
                error = i18next.t('error_fields');
            }

            (<any>this.$root).$refs.dialogMessage.show({
                title: title,
                text: error
            });

            if (this.gaAlreadySubmitted.indexOf('flights.booking.step1.error') < 0) {
                (<any>this).$ga.sendEvent('flights.booking', 'step1.error', 'flights.booking.step1.error', this.gaParams);
                (<any>this).$ga.reachGoal('flights.booking.step1.error');

                this.gaAlreadySubmitted.push('flights.booking.step1.error');
            }
        } else {
            this.$emit('submit');
        }
    }

    showTooltip(target, text) {
        (<any>this).$refs.tooltip.isActive = false;

        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
        }

        this.$nextTick(() => {
            this.tooltipText = text;
            this.tooltipActivator = target;
            (<any>this).$refs.tooltip.isActive = true;

            this.tooltipTimeout = window.setTimeout(() => {
                (<any>this).$refs.tooltip.isActive = false;
            }, 2000);
        });
    }
}
